import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { FiSearch, FiX, FiMenu, FiUser, FiLogOut, FiHeart, FiChevronDown } from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { contentAPI } from "../api/endpoints";
import ImageWithPlaceholder from "./ImageWithPlaceholder";

export default function Navbar({ settings }) {
  const { user, isLoggedIn, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [topSearch, setTopSearch] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const searchRef = useRef(null);
  const userMenuRef = useRef(null);
  const searchContainerRef = useRef(null);
  const debounceRef = useRef(null);
  const topSearchLoaded = useRef(false);

  // Scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setUserMenuOpen(false);
  }, [location]);

  // Close user menu on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Focus search input when opened + load top search
  useEffect(() => {
    if (searchOpen) {
      if (searchRef.current) searchRef.current.focus();
      // Load top search once
      if (!topSearchLoaded.current) {
        topSearchLoaded.current = true;
        contentAPI
          .getTopSearch()
          .then((res) => {
            const data = res.data;
            let items = [];
            if (Array.isArray(data) && data.length > 0 && data[0]?.data) {
              data.forEach((group) => {
                if (Array.isArray(group.data)) items.push(...group.data);
              });
            } else if (Array.isArray(data)) {
              items = data;
            } else if (data && typeof data === "object") {
              items = data.data || data.movies || data.results || [];
            }
            setTopSearch(items.slice(0, 8));
          })
          .catch(() => { });
      }
    }
  }, [searchOpen]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setSuggestions([]);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Debounced live search
  const fetchSuggestions = useCallback((q) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim() || q.trim().length < 2) {
      setSuggestions([]);
      setSuggestionsLoading(false);
      return;
    }
    setSuggestionsLoading(true);
    debounceRef.current = setTimeout(() => {
      contentAPI
        .search(q.trim())
        .then((res) => {
          const data = res.data;
          // Response is grouped by genre: [{ title, type, data: [movies] }]
          let items = [];
          if (Array.isArray(data) && data.length > 0 && data[0]?.data) {
            data.forEach((group) => {
              if (Array.isArray(group.data)) items.push(...group.data);
            });
          } else if (Array.isArray(data)) {
            items = data;
          } else {
            items = data?.results || data?.data || [];
          }
          // Deduplicate by id
          const seen = new Set();
          items = items.filter((item) => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
          });
          setSuggestions(items.slice(0, 6));
        })
        .catch(() => setSuggestions([]))
        .finally(() => setSuggestionsLoading(false));
    }, 400);
  }, []);

  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearchQuery(val);
    fetchSuggestions(val);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery("");
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (item) => {
    const itemType = item.type || "movie";
    navigate(`/details/${itemType}/${item.id}`);
    setSearchOpen(false);
    setSearchQuery("");
    setSuggestions([]);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const navLinks = [
    { to: "/", label: "Home" },
    { to: "/tvshows", label: "TV Shows" },
    { to: "/movies", label: "Movies" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? "bg-dark-900/98 shadow-lg" : "bg-dark-900/90"
        } backdrop-blur-xl border-b border-white/5`}
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img
              src={settings?.logo_url || "/images/large_icon.png"}
              alt="The Slay Network"
              className="h-10 md:h-12 object-contain"
              onError={(e) => {
                e.target.onerror = null;
                e.target.style.display = "none";
                if (e.target.nextElementSibling) e.target.nextElementSibling.style.display = "block";
              }}
            />
            <div style={{ display: "none" }}>
              <span className="text-xl font-extrabold bg-gradient-to-r from-primary to-green-400 bg-clip-text text-transparent">
                THE SLAY NETWORK
              </span>
              <p className="text-[0.6rem] uppercase tracking-[3px] text-gray-500 -mt-1">
                Stream Freely
              </p>
            </div>

          </Link>

          {/* Desktop Nav Links */}
          <ul className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className={`text-sm font-medium transition-colors relative pb-1 ${isActive(link.to) ? "text-white" : "text-gray-400 hover:text-white"
                    }`}
                >
                  {link.label}
                  {isActive(link.to) && (
                    <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded" />
                  )}
                </Link>
              </li>
            ))}
            <li>
              <button
                onClick={() => navigate("/live-tv")}
                className="text-sm font-medium text-gray-400 hover:text-white flex items-center gap-1.5"
              >
                <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
                Live TV
              </button>
            </li>
          </ul>

          {/* Right Actions */}
          <div className="flex items-center gap-3">
            {/* Play Store Button */}
            {settings?.playstore_url && (
              <a
                href={settings.playstore_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 border border-white/20 rounded-full text-xs text-gray-300 hover:border-white/40 hover:text-white transition-all"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3.5 h-3.5">
                  <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.199l2.302 2.302-2.302 2.302-2.698-2.302 2.698-2.302zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.634z" />
                </svg>
                Get App
              </a>
            )}

            {/* Search Toggle */}
            <button
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 text-gray-400 hover:text-white transition-colors"
            >
              {searchOpen ? <FiX size={20} /> : <FiSearch size={20} />}
            </button>

            {/* Auth Buttons */}
            {isLoggedIn ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 hover:bg-white/10 transition-colors"
                >
                  <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center">
                    <FiUser size={14} className="text-primary" />
                  </div>
                  <span className="hidden sm:block text-sm text-gray-300">{user?.name?.split(" ")[0]}</span>
                  <FiChevronDown size={14} className="text-gray-500" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-52 bg-dark-700 border border-dark-400 rounded-xl shadow-2xl overflow-hidden">
                    <div className="p-3 border-b border-dark-400">
                      <p className="text-sm font-medium text-white truncate">{user?.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    </div>
                    <div className="py-1">
                      <Link to="/profile" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5">
                        <FiUser size={15} /> Profile
                      </Link>
                      <Link to="/watchlist" className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5">
                        <FiHeart size={15} /> Watchlist
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-red-400 hover:bg-white/5 w-full"
                      >
                        <FiLogOut size={15} /> Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <Link to="/login" className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors">
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="btn-play-gradient-sm"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="md:hidden p-2 text-gray-400 hover:text-white"
            >
              {mobileOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>
          </div>
        </div>

        {/* Search Bar (expandable) with live suggestions */}
        {searchOpen && (
          <div className="pb-4 relative" ref={searchContainerRef}>
            <form onSubmit={handleSearch} className="relative">
              <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
              <input
                ref={searchRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchInput}
                placeholder="Search movies, TV shows..."
                className="w-full pl-12 pr-4 py-3 bg-dark-600 border border-dark-400 rounded-xl text-white text-sm placeholder:text-gray-500 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
              />
            </form>

            {/* Dropdown — top search when empty, live results when typing */}
            {(() => {
              const isTyping = searchQuery.trim().length >= 2;
              const dropdownItems = isTyping ? suggestions : topSearch;
              const showDropdown = isTyping ? (dropdownItems.length > 0 || suggestionsLoading) : dropdownItems.length > 0;

              if (!showDropdown) return null;

              return (
                <div className="absolute left-0 right-0 top-full mt-1 bg-dark-700 border border-dark-400 rounded-xl shadow-2xl overflow-hidden z-50 max-h-[70vh] overflow-y-auto">
                  {/* Header for top search */}
                  {!isTyping && (
                    <div className="px-4 py-2.5 border-b border-dark-400">
                      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Trending</p>
                    </div>
                  )}

                  {suggestionsLoading && isTyping && dropdownItems.length === 0 ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : (
                    <>
                      {dropdownItems.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => handleSuggestionClick(item)}
                          className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 cursor-pointer transition-colors"
                        >
                          <div className="w-10 h-14 rounded-md overflow-hidden bg-dark-800 shrink-0">
                            <ImageWithPlaceholder
                              src={item.image || item.poster_image || item.poster_url}
                              alt={item.title || item.name}
                              className="w-full h-full object-cover"
                              iconSize={12}
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{item.title || item.name}</p>
                            <p className="text-xs text-gray-500 capitalize">
                              {item.type || "movie"}
                              {item.genres ? ` · ${item.genres}` : ""}
                            </p>
                          </div>
                        </div>
                      ))}
                      {isTyping && searchQuery.trim() && (
                        <div
                          onClick={handleSearch}
                          className="flex items-center gap-2 px-4 py-3 border-t border-dark-400 text-primary text-sm hover:bg-white/5 cursor-pointer transition-colors"
                        >
                          <FiSearch size={14} />
                          See all results for "{searchQuery}"
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="md:hidden pb-5 border-t border-dark-400 pt-4 space-y-1 max-h-[calc(100vh-4rem)] overflow-y-auto">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`block px-4 py-3 rounded-lg text-sm font-medium ${isActive(link.to) ? "bg-white/5 text-white" : "text-gray-400"
                  }`}
              >
                {link.label}
              </Link>
            ))}
            <button
              onClick={() => navigate("/live-tv")}
              className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-gray-400 w-full"
            >
              <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse" />
              Live TV
            </button>
            {!isLoggedIn && (
              <div className="pt-3 space-y-2">
                <Link to="/login" onClick={() => setMobileOpen(false)} className="block text-center px-4 py-3 text-sm text-gray-300 border border-dark-400 rounded-lg">
                  Sign In
                </Link>
                <Link to="/register" onClick={() => setMobileOpen(false)} className="block text-center btn-play-gradient-sm">
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
